/**
 * @file FilterModalV2.js
 * @description A modal component for filtering a list of unique options extracted from a table's column. 
 * If the list consists of an array of dates in the format YYYY-MM-DDThh:mm:ssZ, then the filter will be processed as a nested array of date options (years, months, and days).
 * 
 * This component allows users to filter options based on years, months, and days, with the ability to select all options or clear the filter. 
 * It also includes a search bar for filtering string items.
 * 
 * @prop {string} column - The column name for storage reference. Example: "name"
 * @prop {Array} options - The list of options available for filtering. Example: ["Option 1", "Option 2"]
 * @prop {Array} selectedOptions - The currently selected options for the filter. Example: ["Option 1"]
 * @prop {function} onApply - Callback function called when the user applies the filter. Example: (column, values) => { ... }
 * @prop {function} onClose - Callback function called to close the modal. Example: () => { ... }
 * @prop {function} [transformOptions] - Optional function to convert values for display. Example: (integerValue) => integerValue.toString()
 * 
 * @returns {JSX.Element} Renders a modal with a search bar, checkboxes for options, and buttons to apply or clear filters.
 * 
 * @example
 * const ParentComponent = () => {
 *   const [selectedOptions, setSelectedOptions] = useState({});
 *   const [activeModal, setActiveModal] = useState(null);
 *   
 *   const array = [{id: 1, name: "Item 1"}, {id: 2, name: "Item 2"}, {id: 2, name: "Item 2"}];
 *  
 *   const openModal = (column) => {
 *       setActiveModal(column);
 *   };
 *   
 *   const closeModal = () => {
 *       setActiveModal(null);
 *   };
 *   
 *   const handleApply = (column, values) => {
 *       setSelectedOptions(prev => ({
 *           ...prev,
 *           [column]: values
 *       }));
 *   };
 *  
 *  const renderFilterModal = (column, options, transformOptions) => {
 *        const isSelected = selectedOptions[column] && selectedOptions[column].length > 0;
 *        return (
 *            <>
 *                <span onClick={() => openModal(column)} className={styles.cursorPointer}>
 *                    {isSelected ? <svg
 *                    style={{ 
 *                        marginTop: '4px', 
 *                        display: 'inline-block', 
 *                        width: '1em', 
 *                        height: '1em' 
 *                    }}
 *                    >
 *                        <path d="M1.08871 2.81875C0.701376 2.38585 0.507709 2.1694 0.500404 1.98544C0.494058 1.82564 0.562731 1.67205 0.686053 1.57022C0.828016 1.453 1.11846 1.453 1.69935 1.453H9.30065C9.88154 1.453 10.172 1.453 10.3139 1.57022C10.4373 1.67205 10.5059 1.82564 10.4996 1.98544C10.4923 2.1694 10.2986 2.38585 9.91129 2.81875L6.98903 6.0848C6.91182 6.17109 6.87322 6.21424 6.84569 6.26335C6.82128 6.3069 6.80336 6.35378 6.79252 6.40252C6.78029 6.45747 6.78029 6.51537 6.78029 6.63116V9.36948C6.78029 9.46963 6.78029 9.5197 6.76414 9.563C6.74986 9.60125 6.72665 9.63554 6.69643 9.663C6.66223 9.69408 6.61574 9.71268 6.52275 9.74987L4.78156 10.4463C4.59333 10.5216 4.49922 10.5593 4.42367 10.5436C4.35761 10.5299 4.29963 10.4906 4.26235 10.4344C4.21971 10.37 4.21971 10.2687 4.21971 10.066V6.63116C4.21971 6.51537 4.21971 6.45747 4.20748 6.40252C4.19664 6.35378 4.17872 6.3069 4.15431 6.26335C4.12678 6.21424 4.08818 6.17109 4.01096 6.0848L1.08871 2.81875Z" fill="#1366FF" stroke="#1366FF" stroke-linecap="round" stroke-linejoin="round"/>
 *                    </svg> : 
 *                    <i className="bi bi-funnel" style={{ fontSize: "0.7em" }}></i>}
 *                </span>
 *                {activeModal === column && (
 *                    <FilterModalV2
 *                        column={column}
 *                        options={Array.from(new Set(options))}
 *                        selectedOptions={selectedOptions[column] || []}
 *                        onApply={handleApply}
 *                        onClose={closeModal}
 *                        transformOptions={transformOptions}
 *                    />
 *                )}
 *            </>
 *        );
 *    };
 *  
 *   return (
 *     <>
 *          <span onClick={() => openModal("columnLabel")} className={styles.cursorPointer}>
 *              <i className="bi bi-funnel" style={{ fontSize: "0.7em" }}></i>
 *          </span>
 *          {renderFilterModal("columnLabel", array.map(item => item.name))}
 *     </>
 *   );
 * };
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import "../helpers/FilterComponent.css";
import { getText } from '../../i18n';

const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const SELECT_ALL = "Select All";

export const FilterModalV2 = ({
    column, options, selectedOptions, onApply, onClose, transformOptions = null
}) => {
    const [tempSelectedOptions, setTempSelectedOptions] = useState(selectedOptions);
    const [searchTerm, setSearchTerm] = useState("");
    const modalRef = useRef(null);
    const [expandedYears, setExpandedYears] = useState({});
    const [expandedMonths, setExpandedMonths] = useState({});
    const initialOrderRef = useRef(null);

    useEffect(() => {
        setTempSelectedOptions(selectedOptions);
    }, [selectedOptions]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [modalRef, onClose]);

    const handleSelectionChange = (option) => {
        if (option === SELECT_ALL) {
            const allSelected = isAllSelected; // Accessing isAllSelected as a value
            setTempSelectedOptions(allSelected ? [] : getAllSelections());
        }
    };

    const getAllSelections = () => {
        const selections = new Set();
    
        sortedOptions.forEach(year => {
            const yearKey = `${year.year}`;
            selections.add(yearKey);
            year.months.forEach(month => {
                const monthKey = `${String(month.month).padStart(2, '0')}-${year.year}`; // MM-YYYY format
                selections.add(monthKey);
                month.days.forEach(day => {
                    selections.add(`${String(month.month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year.year}`); // MM-DD-YYYY format
                });
            });
        });
    
        stringItems.forEach(item => selections.add(item));
    
        return Array.from(selections);
    };
    

    const handleApply = () => {
        console.log(tempSelectedOptions);
        const filteredArray = tempSelectedOptions.filter(item => {
            if (/^\d{4}$/.test(item)) {
                return !isIndeterminate(parseInt(item));
            }
            return true;
        });
        onApply(column, filteredArray);
        onClose();
    };

    const handleClearFilter = () => {
        setTempSelectedOptions([]);
        onApply(column, []);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const parseDates = (dates) => {
        const dateMap = {};
        const stringItems = [];

        dates.forEach(date => {
            if (date == null || date === "") return;

            if (Array.isArray(date)) {
                stringItems.push(...date);
            } else if (isValidDate(date)) {
                const { year, month, day } = extractDateComponents(date);
                if (!dateMap[year]) dateMap[year] = {};
                if (!dateMap[year][month]) dateMap[year][month] = new Set();
                dateMap[year][month].add(day);
            } else {
                stringItems.push(date);
            }
        });

        return { dateMap, stringItems };
    };

    const isValidDate = (date) => {
        return typeof date === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.*$/.test(date);
    };    
    
    const containsDates = useMemo(() => options.some(isValidDate), [options]);

    const extractDateComponents = (date) => {
        const d = new Date(date);
        return {
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate()
        };
    };

    const { dateMap, stringItems } = useMemo(() => parseDates(options), [options]);

    const sortedOptions = useMemo(() => {
        return Object.keys(dateMap)
            .sort((a, b) => b - a) // Sort years in descending order
            .map(year => {
                const months = dateMap[year];
                const monthEntries = Object.keys(months)
                    .sort((a, b) => a - b) // Sort months in ascending order
                    .map(month => {
                        const days = Array.from(months[month]).sort((a, b) => a - b); // Sort days in ascending order
                        return { month, days };
                    });
                return { year, months: monthEntries };
            });
    }, [dateMap]);

    const sortedStringItems = useMemo(() => {
        return stringItems.sort((a, b) => {
            const displayValueA = transformOptions ? transformOptions(a) : a;
            const displayValueB = transformOptions ? transformOptions(b) : b;
            return displayValueA.localeCompare(displayValueB, undefined, { sensitivity: 'base' });
        });
    }, [stringItems, transformOptions]);

    const filteredStringItems = useMemo(() => {
        if (!searchTerm) return sortedStringItems;
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return sortedStringItems.filter(item => {
            const displayValue = transformOptions ? transformOptions(item) : item;
            return displayValue.toString().toLowerCase().includes(lowerCaseSearchTerm);
        });
    }, [sortedStringItems, searchTerm, transformOptions]);

    const isAllSelected = useMemo(() => {
        const totalSelections = sortedOptions.flatMap(year => 
            year.months.flatMap(month => month.days.map(day => `${month.month}-${year.year}-${day}`))
        ).length + 
        sortedOptions.flatMap(year => `${year.year}`).length + 
        sortedOptions.flatMap(year => year.months.map(month => `${month.month}-${year.year}`)).length + 
        filteredStringItems.length;

        return tempSelectedOptions.length === totalSelections;
    }, [tempSelectedOptions, sortedOptions, filteredStringItems]);

    const isIndeterminate = (year) => {
        const months = dateMap[year];
        const allMonths = Object.keys(months);
        const checkedMonths = allMonths.filter(month => tempSelectedOptions.includes(`${String(month).padStart(2, '0')}-${year}`));
        
        return checkedMonths.length > 0 && (checkedMonths.length < allMonths.length || allMonths.some(month => isMonthIndeterminate(year, month)));
    };

    const isMonthIndeterminate = (year, month) => {
        const days = dateMap[year][month];
        const checkedDays = days && Array.from(days).filter(day => tempSelectedOptions.includes(`${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`));
        return checkedDays.length > 0 && checkedDays.length < days.size;
    };

    const toggleDaySelection = (year, month, day) => {
        const dayKey = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`; // MM-DD-YYYY format
        setTempSelectedOptions(prev => {
            const newSelection = prev.includes(dayKey) ? prev.filter(item => item !== dayKey) : [...prev, dayKey];
            return updateMonthAndYearSelection(year, month, newSelection);
        });
    };
    

    const toggleMonthSelection = (year, month) => {
        const days = dateMap[year][month];
        const monthKey = `${String(month).padStart(2, '0')}-${year}`; // MM-YYYY format
        const allDaysSelected = days && Array.from(days).every(day => tempSelectedOptions.includes(`${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`));
    
        setTempSelectedOptions(prev => {
            const newSelection = allDaysSelected ? 
                prev.filter(item => item !== monthKey && !new RegExp(`^${String(month).padStart(2, '0')}-\\d{2}-${year}$`).test(item)) : 
                [...prev, monthKey, ...Array.from(days).map(day => `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`)];
            return updateYearSelection(year, newSelection);
        });
    };
    

    const toggleYearSelection = (year) => {
        const months = dateMap[year];
        const yearKey = `${year}`;
        const allMonthsSelected = Object.keys(months).every(month => tempSelectedOptions.includes(`${String(month).padStart(2, '0')}-${year}`));

        setTempSelectedOptions(prev => {
            const newSelection = allMonthsSelected ? 
                prev.filter(item => !item.includes(`${year}`)) : 
                [
                    ...prev,
                    yearKey,
                    ...Object.keys(months).flatMap(month => {
                        const days = Array.from(months[month]);
                        return [
                            `${String(month).padStart(2, '0')}-${year}`,
                            ...days.map(day => `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`)
                        ];
                    })
                ];
            return newSelection;
        });
    };

    const updateMonthAndYearSelection = (year, month, newSelection) => {
        const monthKey = `${String(month).padStart(2, '0')}-${year}`;
        const days = dateMap[year][month];
        const allDaysSelected = days && Array.from(days).every(day => newSelection.includes(`${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`));
        const allDaysUnchecked = days && Array.from(days).every(day => !newSelection.includes(`${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`));

        if (allDaysSelected && !newSelection.includes(monthKey)) {
            newSelection.push(monthKey);
        } else if (allDaysUnchecked && newSelection.includes(monthKey)) {
            newSelection = newSelection.filter(item => item !== monthKey);
        }

        return updateYearSelection(year, newSelection);
    };

    const updateYearSelection = (year, newSelection) => {
        const months = dateMap[year];
        const allMonthsSelected = Object.keys(months).every(month => newSelection.includes(`${String(month).padStart(2, '0')}-${year}`));
        const allMonthsUnchecked = Object.keys(months).every(month => !newSelection.includes(`${String(month).padStart(2, '0')}-${year}`));

        if (allMonthsSelected && !newSelection.includes(`${year}`)) {
            newSelection.push(`${year}`);
        } else if (allMonthsUnchecked && newSelection.includes(`${year}`)) {
            newSelection = newSelection.filter(item => item !== `${year}`);
        }

        return newSelection;
    };

    const toggleYearExpansion = (year) => {
        setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
    };

    const toggleMonthExpansion = (year, month) => {
        setExpandedMonths(prev => ({ ...prev, [`${String(month).padStart(2, '0')}-${year}`]: !prev[`${String(month).padStart(2, '0')}-${year}`] }));
    };

    const noResultsFound = useMemo(() => {
        return sortedOptions.length === 0 && filteredStringItems.length === 0;
    }, [sortedOptions, filteredStringItems]);

    const combinedFilteredItems = useMemo(() => {
        if (initialOrderRef.current === null) {
            const initiallySelected = sortedStringItems.filter(item => selectedOptions.includes(item));
            const initiallyUnselected = sortedStringItems.filter(item => !selectedOptions.includes(item));
            initialOrderRef.current = [...initiallySelected, ...initiallyUnselected];
        }
        return initialOrderRef.current;
    }, [sortedStringItems]);

    const filteredCombinedItems = useMemo(() => {
        if (!searchTerm) return combinedFilteredItems;
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return combinedFilteredItems.filter(item => {
            const displayValue = transformOptions ? transformOptions(item) : item;
            return displayValue.toString().toLowerCase().includes(lowerCaseSearchTerm);
        });
    }, [combinedFilteredItems, searchTerm, transformOptions]);

    const areAllMonthsAndDaysSelected = (year) => {
        const months = dateMap[year]; // Assuming dateMap is available and structured as { year: { month: Set(days) } }
        return Object.keys(months).every(month => {
            const days = Array.from(months[month]);
            // Check if the month is selected
            const monthKey = `${String(month).padStart(2, '0')}-${year}`;
            const isMonthSelected = tempSelectedOptions.includes(monthKey);
            
            // Check if all days in the month are selected
            const allDaysSelected = days.every(day => tempSelectedOptions.includes(`${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`));
            
            // Return true if the month is selected or all days are selected
            return isMonthSelected || allDaysSelected;
        });
    };

    return (
        <div ref={modalRef} onClick={(event) => event.stopPropagation()} className="filterModal">
            {!containsDates && (
                <div className="filterSearchbar">
                    <i className="bi bi-search" />
                    <input onChange={handleSearchChange} type="text" placeholder={getText('msg_bce06414177f')} />
                </div>
            )}
            <div className="filterButtonWrapper">
                <button className="filterModalButton filterModalButtonUnderline float-start" onClick={handleClearFilter} disabled={tempSelectedOptions.length === 0}>
                    {getText('msg_f641dcde74d7')}
                </button>
            </div>
            <hr className="filterDivider" />
            <div className="filterOptionContainer">
                {searchTerm.length <= 0 && (
                    <div className="filterOption">
                        <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={() => handleSelectionChange(SELECT_ALL)}
                        />
                        <label>{SELECT_ALL}</label>
                    </div>
                )}
{sortedOptions.map(({ year, months }, yearIndex) => (
    <div key={yearIndex} className="d-flex flex-direction-column">
        <div className="filterOption">
            <input
                type="checkbox"
                id={`year-${year}`}
                checked={tempSelectedOptions.includes(year.toString()) || areAllMonthsAndDaysSelected(year)}
                onChange={() => toggleYearSelection(year)}
                ref={el => {
                    if (el) {
                        el.indeterminate = isIndeterminate(year);
                    }
                }}
            />
            <label>
                {year}
                {months.length > 0 && (
                    <span className={`chevron ${expandedYears[year] ? 'expanded' : ''}`} onClick={(e) => { e.stopPropagation(); toggleYearExpansion(year); }}>
                        <i className={`caret bi bi-chevron-${expandedYears[year] ? 'up' : 'down'}`}></i>
                    </span>
                )}
            </label>
        </div>
        {expandedYears[year] && (
            <div className="nestedOptions">
                {months.map(({ month, days }, monthIndex) => (
                    <div key={monthIndex} className="d-flex flex-direction-column">
                        <div className="filterOption" style={{ marginLeft: "16px" }}>
                            <input
                                type="checkbox"
                                id={`month-${String(month).padStart(2, '0')}-${year}`}
                                checked={tempSelectedOptions.includes(`${String(month).padStart(2, '0')}-${year}`)}
                                onChange={() => toggleMonthSelection(year, month)}
                                ref={el => {
                                    if (el) {
                                        el.indeterminate = isMonthIndeterminate(year, month);
                                    }
                                }}
                            />
                            <label>
                                {monthNames[month - 1]}
                                {days.length > 0 && (
                                    <span className={`chevron ${expandedMonths[`${String(month).padStart(2, '0')}-${year}`] ? 'expanded' : ''}`} onClick={(e) => { e.stopPropagation(); toggleMonthExpansion(year, month); }}>
                                        <i className={`caret bi bi-chevron-${expandedMonths[`${String(month).padStart(2, '0')}-${year}`] ? 'up' : 'down'}`}></i>
                                    </span>
                                )}
                            </label>
                        </div>
                        {expandedMonths[`${String(month).padStart(2, '0')}-${year}`] && (
                            <div className="nestedOptions">
                                {days.map(day => (
                                    <div key={day} className="filterOption" style={{ marginLeft: "32px" }}>
                                        <input
                                            type="checkbox"
                                            id={`day-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`}
                                            checked={tempSelectedOptions.includes(`${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`)}
                                            onChange={() => toggleDaySelection(year, month, day)}
                                        />
                                        <label>{day}</label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )}
    </div>
))}

                {filteredCombinedItems.length > 0 && (
                    <div>
                        {filteredCombinedItems.map((item, index) => {
                            const displayValue = transformOptions ? transformOptions(item) : item;
                            return (
                                <div key={index} className="filterOption">
                                    <input
                                        type="checkbox"
                                        id={`item-${index}`}
                                        checked={tempSelectedOptions.includes(item)}
                                        onChange={() => {
                                            setTempSelectedOptions(prev =>
                                                prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
                                            );
                                        }}
                                    />
                                    <label>{displayValue}</label>
                                </div>
                            );
                        })}
                    </div>
                )}
                {noResultsFound && (
                    <div className="noResultsLabel">{getText('msg_728b4ab4c02f')}</div>
                )}
            </div>
            <hr className="filterDivider" />
            <div className="filterButtonWrapper">
                <button className="filterModalButton filterModalButtonBold float-end" onClick={handleApply}>{getText('msg_cfea419c3b4e')}</button>
            </div>
        </div>
    );
};
